import React from "react";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import Button from "components/CustomButton/CustomButton.jsx";

const HOME_PATH = "/admin/briefing-room";

const reloadPage = () => window.location.reload();

// What the volunteer sees instead of a blank page when something crashes.
// `inApp` = rendered inside the router and layout, so "Go to Briefing Room"
// can navigate in place; otherwise it falls back to a full page load.
export const ErrorScreen = ({ inApp, onGoHome }) => (
  <div
    role="alert"
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      minHeight: inApp ? "calc(100vh - 135px)" : "100vh",
      padding: "16px",
    }}
  >
    <ExclamationTriangleIcon style={{ width: "100px", height: "100px" }} />
    <h1>
      <b>Something went wrong</b>
    </h1>
    <p
      style={{
        textAlign: "center",
        maxWidth: "400px",
        color: "#666",
        marginTop: "16px",
      }}
    >
      This page ran into a problem and couldn't be shown. The error has been
      reported. Try reloading the page, or go back to the Briefing Room.
    </p>
    <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
      <Button bsStyle="info" fill onClick={reloadPage}>
        Reload page
      </Button>
      {inApp ? (
        <Link to={HOME_PATH} className="btn btn-default" onClick={onGoHome}>
          Go to Briefing Room
        </Link>
      ) : (
        <a href={HOME_PATH} className="btn btn-default">
          Go to Briefing Room
        </a>
      )}
    </div>
  </div>
);

// Last resort around the whole app: a crash outside the pages (layout,
// sidebar, auth) shows the error screen instead of unmounting everything.
export const AppErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary fallback={<ErrorScreen />}>
    {children}
  </Sentry.ErrorBoundary>
);

// Around the routed page content, so a crashing page keeps the sidebar and
// navbar usable. `resetKey` (the current path) is used as the React key, so
// navigating to another page mounts a fresh boundary and clears the error.
export const PageErrorBoundary = ({ resetKey, children }) => (
  <Sentry.ErrorBoundary
    key={resetKey}
    fallback={({ resetError }) => <ErrorScreen inApp onGoHome={resetError} />}
  >
    {children}
  </Sentry.ErrorBoundary>
);
